'use client';

import { RefObject, useLayoutEffect, useRef, useState, ComponentProps } from 'react';
import { useMediaQuery, useScrollTrigger } from '@mui/material';
import { useTheme, alpha, styled } from '@classify/react-core-components/styles';
import Box from '@classify/react-core-components/Box';
import { translate } from '@openclassrooms/translations';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import Typography from '@classify/react-core-components/Typography';
import SectionNav from '@classify/react-core-components/SectionNav';
import CourseActionButton from '../Hero/CourseActionButton';
import COURSE_TABS from '../../_constants/courseTabs';
import useCourseStatus from '../../_hooks/useCourseStatus';
import useIsElementVisible from '../../_hooks/useIsElementVisible';
import { useCourseContext } from '../../_contexts/CourseContext';
const COURSE_TABBAR_HEIGHT = 60;
interface Props {
  footerRef: RefObject<HTMLDivElement>;
  userId?: number;
}
const Root = styled('div', {
  shouldForwardProp: prop => !['categoryColor', 'isSticky'].includes(prop as string)
})<{
  categoryColor: string;
  isSticky: boolean;
}>(({
  categoryColor,
  isSticky,
  theme: {
    zIndex,
    palette,
    breakpoints,
    spacing,
    shadows
  }
}) => ({
  position: 'sticky',
  top: '0',
  display: 'block',
  width: '100%',
  backgroundColor: palette.background.primary,
  borderBottom: `1px solid ${alpha(categoryColor, 0.24)}`,
  zIndex: zIndex.appBar - 1,
  paddingTop: spacing(2),
  ...(isSticky && {
    backgroundColor: palette.background.default,
    borderBottom: 0,
    boxShadow: shadows[4],
    maxHeight: COURSE_TABBAR_HEIGHT,
    [breakpoints.up('sm')]: {
      maxHeight: 'unset'
    }
  })
}));
const Wrapper = styled(ContentWrapper)(({
  theme: {
    breakpoints
  }
}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  height: COURSE_TABBAR_HEIGHT,
  [breakpoints.up('sm')]: {
    height: 'unset'
  }
}));
const Title = styled(Typography)(({
  theme: {
    breakpoints
  }
}) => ({
  [breakpoints.up('sm')]: {
    maxWidth: 481,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  [breakpoints.up('md')]: {
    maxWidth: 710
  },
  [breakpoints.up('lg')]: {
    maxWidth: 940
  }
}));
const TabBar = ({
  userId,
  footerRef
}: Props) => {
  const {
    breakpoints
  } = useTheme();
  const isDesktop = useMediaQuery(breakpoints.up('sm'));
  const tabsRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const {
    course
  } = useCourseContext();
  const {
    isFinished,
    hasAlreadyFollowed,
    notStarted,
    isFollowing
  } = useCourseStatus(course);
  const {
    title: courseTitle,
    categoryColor
  } = course;
  const TAB_SCROLL_OFFSET = 16;
  const scrollToOffset = (tabsRef?.current?.clientHeight ?? 0) + TAB_SCROLL_OFFSET || 0;
  const isFooterVisible = useIsElementVisible(footerRef, 0.5);
  useLayoutEffect(() => {
    if (!tabsRef.current) {
      return;
    }
    setOffset(tabsRef.current.offsetTop);
  }, [tabsRef]);
  const hasScrolledToTop = useScrollTrigger({
    disableHysteresis: true,
    threshold: offset
  });
  const shouldDisplayCourseTitle = hasScrolledToTop && isDesktop;
  const shouldDisplayCta = hasScrolledToTop && !isFooterVisible && isDesktop && !isFinished;
  const {
    TAB_OVERVIEW,
    TAB_TOC,
    TAB_CREDITS
  } = COURSE_TABS;
  const items = [{
    label: translate('course.overview.tab'),
    targetId: TAB_OVERVIEW
  }, {
    label: translate('course.toc.tab'),
    targetId: TAB_TOC
  }, {
    label: translate('course.teachers.tab'),
    targetId: TAB_CREDITS
  }];
  const getCourseActionProps = (): ComponentProps<typeof CourseActionButton> | null => {
    if (notStarted) return {
      action: 'start',
      userId
    };
    if (isFollowing) return {
      action: 'continue',
      userId
    };
    if (hasAlreadyFollowed && userId) return {
      action: 'follow',
      userId
    };
    return null;
  };
  const courseActionProps = getCourseActionProps();
  return <Root ref={tabsRef} categoryColor={categoryColor} isSticky={hasScrolledToTop}>
      <Wrapper>
        <div>
          {shouldDisplayCourseTitle && <Title variant="h6" component="p">
              {courseTitle}
            </Title>}
          <SectionNav items={items} color={categoryColor} offset={scrollToOffset} />
        </div>
        {shouldDisplayCta && courseActionProps && <Box sx={{
        alignSelf: 'center'
      }}>
            <CourseActionButton {...courseActionProps} />
          </Box>}
      </Wrapper>
    </Root>;
};
export default TabBar;